"use client"

import React, { useState, useMemo, useEffect, useCallback } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBox, faClock, faExclamationCircle, faCheckCircle } from "@fortawesome/free-solid-svg-icons"
import DashboardStats from "../../components/inventory/sections/DashboardStats"
import Card from "../../components/inventory/modules/Card/Card"
import SearchBar from "../../components/shared-modules/SearchBar"
import Table from "../../components/inventory/modules/table/table"
import Tabs from "../../components/inventory/modules/Tabs/Tabs"
import Badge from "../../components/inventory/modules/Badge/Badge"
import styles from "../../styles/inventory/orders-page.module.css"
import { getWarrantyDevices, getRefurbishedDevices } from "../../services/inventory/movement"
import { parseDateSafe } from "../../utils/dateFormatters"

const transformLocal = (d) => ({
  id: d.id,
  imei: d.imei || d.serial || String(d.id),
  type: (d.device_type && (d.device_type.name || d.device_type)) || d.device_type_name || "Unknown",
  purchaseDate: d.purchase_date || d.purchaseDate || "N/A",
  warrantyEnd: d.warranty_end || d.warrantyEnd || "N/A",
  status: d.inventory_status || d.status || d.device_status || "N/A",
  claimed: d.warranty_claim_status || (d.claimed ? "Claimed" : "Not Claimed") || "Not Claimed",
  original: d,
})

const computeKpis = (rows = []) => {
  const now = new Date()
  const expiringSoonCutoff = new Date(now)
  expiringSoonCutoff.setDate(now.getDate() + 30)

  let valid = 0
  let expiringSoon = 0
  let expired = 0
  let claimed = 0

  rows.forEach((r) => {
    const end = parseDateSafe(r.warrantyEnd || r.warranty_end)
    if (!end) return
    if ((String(r.claimed || "") || "").toLowerCase().includes("claim")) claimed++
    if (end < now) expired++
    else {
      valid++
      if (end <= expiringSoonCutoff) expiringSoon++
    }
  })

  return [
    { title: "Valid Warranties", value: valid, changeType: "neutral", iconColor: "#10b981", icon: <FontAwesomeIcon icon={faCheckCircle} /> },
    { title: "Expiring Soon", value: expiringSoon, changeType: "warning", iconColor: "#f59e0b", icon: <FontAwesomeIcon icon={faClock} /> },
    { title: "Expired", value: expired, changeType: "negative", iconColor: "#ef4444", icon: <FontAwesomeIcon icon={faExclamationCircle} /> },
    { title: "Warranty Claimed", value: claimed, changeType: "neutral", iconColor: "#3b82f6", icon: <FontAwesomeIcon icon={faBox} /> },
  ]
}

const statusVariant = (s) => {
  const st = (s || "").toLowerCase()
  if (st.includes("pending")) return { variant: "warning", label: s }
  if (st.includes("assigned")) return { variant: "info", label: s }
  if (st.includes("refurb")) return { variant: "success", label: s }
  if (st.includes("expired")) return { variant: "danger", label: s }
  return { variant: "default", label: s }
}

const WarrantyPage = () => {
  const [tab, setTab] = useState("refurbished")
  const [search, setSearch] = useState("")

  const [warrantyApiData, setWarrantyApiData] = useState({ meta: null, results: [] })
  const [warrantyLoading, setWarrantyLoading] = useState(false)
  const [warrantyError, setWarrantyError] = useState(null)

  const [refurbApiData, setRefurbApiData] = useState({ meta: null, results: [] })
  const [refurbLoading, setRefurbLoading] = useState(false)
  const [refurbError, setRefurbError] = useState(null)

  const fetchWarranty = useCallback(async (pageOrUrl = null, claimStatus = null) => {
    setWarrantyLoading(true)
    setWarrantyError(null)
    try {
      const res = await getWarrantyDevices(pageOrUrl, claimStatus)
      const payload = Array.isArray(res) ? { meta: null, results: res } : res ?? { meta: null, results: [] }
      setWarrantyApiData(payload)
    } catch (err) {
      console.error("Failed to load warranty list:", err)
      setWarrantyApiData({ meta: null, results: [] })
      setWarrantyError(err)
    } finally {
      setWarrantyLoading(false)
    }
  }, [])

  const fetchRefurbished = useCallback(async (pageOrUrl = null) => {
    setRefurbLoading(true)
    setRefurbError(null)
    try {
      const res = await getRefurbishedDevices(pageOrUrl)
      const payload = Array.isArray(res) ? { meta: null, results: res } : res ?? { meta: null, results: [] }
      setRefurbApiData(payload)
    } catch (err) {
      console.error("Failed to load refurbished list:", err)
      setRefurbApiData({ meta: null, results: [] })
      setRefurbError(err)
    } finally {
      setRefurbLoading(false)
    }
  }, [])

  useEffect(() => {
    if (tab === "all") {
      fetchWarranty()
    } else {
      fetchRefurbished()
    }
  }, [tab, fetchWarranty, fetchRefurbished])

  const warrantyRows = useMemo(() => (warrantyApiData?.results || []).map(transformLocal), [warrantyApiData])
  const refurbRows = useMemo(() => (refurbApiData?.results || []).map(transformLocal), [refurbApiData])

  const combinedForKpis = useMemo(() => [...warrantyRows], [warrantyRows])
  const kpiStats = useMemo(() => computeKpis(combinedForKpis), [combinedForKpis])

  const data = useMemo(() => (tab === "all" ? warrantyRows : refurbRows), [tab, warrantyRows, refurbRows])

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase()
    if (!q) return data
    return data.filter((r) => {
      return (
        String(r.imei).toLowerCase().includes(q) ||
        String(r.type).toLowerCase().includes(q) ||
        String(r.status).toLowerCase().includes(q)
      )
    })
  }, [data, search])

  const columns = [
    { key: "imei", label: "IMEI", alignLeft: true },
    { key: "type", label: "Type", alignLeft: true },
    { key: "purchaseDate", label: "Purchase Date", alignLeft: true },
    { key: "warrantyEnd", label: "Warranty End", alignLeft: true },
    {
      key: "status",
      label: "Status",
      render: (row) => {
        const s = statusVariant(row.status)
        return <Badge variant={s.variant} size="small">{s.label}</Badge>
      },
    },
    {
      key: "claimed",
      label: "Warranty Claimed",
      alignLeft: true,
      render: (row) => <Badge variant={(String(row.claimed || "").toLowerCase().includes("claim")) ? "success" : "outline"} size="small">{row.claimed}</Badge>,
    },
  ]

  const activeMeta = tab === "all" ? warrantyApiData?.meta ?? { total_items: warrantyRows.length } : refurbApiData?.meta ?? { total_items: refurbRows.length }
  const activeLoading = tab === "all" ? warrantyLoading : refurbLoading
  const activeError = tab === "all" ? warrantyError : refurbError

  return (
    <div className={styles.orders_page}>
      <div className={styles.page_header}>
        <div className={styles.header_left}>
          <h1 className={styles.page_title}>Warranty Tracking</h1>
          <p className={styles.page_subtitle}>Monitor warranty status of all devices across warehouses</p>
        </div>
      </div>

      <div className={styles.section}>
        <Card
          title={`Warranty Overview`}
          headerRight={(
            <div className={styles.card_header_with_filters}>
              <div className={styles.filters_group} />
            </div>
          )}
        >
          <div className={styles.section}>
            <DashboardStats stats={kpiStats} />
          </div>

          <div className={styles.tabs_container}>
            <Tabs
              tabs={[{ id: "refurbished", label: "Refurbished" }, { id: "all", label: "All" }]}
              activeTab={tab}
              onChange={setTab}
              variant="secondary"
              size="medium"
            />
          </div>

          <div className={styles.card_header_with_filters_2}>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <SearchBar searchTerm={search} onSearchChange={setSearch} placeholder="Search IMEI, type or status..." width="300px" />
            </div>
          </div>

          <div>
            <div className={styles.table_container}>
              <Table
                columns={columns}
                data={filtered}
                height={600}
                pagination={true}
                meta={activeMeta}
                paginationLoading={activeLoading}
                onPageChange={(p) => {
                  if (tab === "all") fetchWarranty(p)
                  else fetchRefurbished(p)
                }}
                isRowClickable={false}
                loading={activeLoading}
              />
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default WarrantyPage
